import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { type ReactNode, useEffect, useState } from 'react';
import { HttpError } from 'react-admin';
import { Link as RouterLink } from 'react-router-dom';
import PublicPage from '../components/PublicPage.tsx';
import { PHONE_HREF, PHONE_LABEL } from '../demo/demoLinks.ts';
import SetPasswordForm from './SetPasswordForm.tsx';
import { type CheckoutStatus, fetchCheckoutStatus } from './signupApi.ts';
import useLinkSecret, { forgetLinkSecret } from './useLinkSecret.ts';

// Stripe's webhook usually lands within seconds of the redirect here. The minute
// runs on its own timer, so a request that hangs cannot hold the page on "Setting up".
const POLL_MS = 2000;
const REQUEST_TIMEOUT_MS = 10_000;
const GIVE_UP_MS = 60_000;

type PageState = CheckoutStatus | { status: 'slow' } | { status: 'unknown' };

const Message = ({ title, body }: { title: string; body: string }) => (
  <Box sx={{ mt: 3 }}>
    <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
    <Typography variant="body2" sx={{ mt: 0.75, color: 'text.secondary' }}>
      {body}
    </Typography>
  </Box>
);

const Actions = ({ children }: { children: ReactNode }) => (
  <Stack direction="row" spacing={1} sx={{ mt: 2.5, justifyContent: 'center' }}>
    {children}
  </Stack>
);

/** Where Stripe sends a buyer after paying: wait for their workspace, then set a password. */
const WelcomePage = () => {
  const sessionId = useLinkSecret('session_id');
  const [polled, setPolled] = useState<PageState>({ status: 'pending' });
  const [attempt, setAttempt] = useState(0);
  const state: PageState = sessionId ? polled : { status: 'unknown' };

  useEffect(() => {
    if (!sessionId) return;
    let done = false;
    let timer: number | undefined;
    let request: AbortController | undefined;

    const stop = () => {
      done = true;
      window.clearTimeout(timer);
      window.clearTimeout(deadline);
      request?.abort();
    };
    const finish = (next: PageState) => {
      stop();
      // Ready or unknown, this checkout has nothing more to give the page.
      if (next.status === 'ready' || next.status === 'unknown') {
        forgetLinkSecret('session_id');
      }
      setPolled(next);
    };
    const deadline = window.setTimeout(() => finish({ status: 'slow' }), GIVE_UP_MS);

    const poll = async () => {
      request = new AbortController();
      const requestTimer = window.setTimeout(() => request?.abort(), REQUEST_TIMEOUT_MS);
      try {
        const next = await fetchCheckoutStatus(sessionId, request.signal);
        if (done) return;
        if (next.status !== 'pending') {
          finish(next);
          return;
        }
      } catch (error) {
        if (done) return;
        if (error instanceof HttpError && error.status === 404) {
          finish({ status: 'unknown' });
          return;
        }
        // A dropped or timed-out request: keep waiting.
      } finally {
        window.clearTimeout(requestTimer);
      }
      timer = window.setTimeout(() => void poll(), POLL_MS);
    };

    void poll();
    return stop;
  }, [sessionId, attempt]);

  if (state.status === 'set-password') {
    return (
      <PublicPage>
        <Message
          title="Welcome to Forge"
          body={`Your workspace for ${state.companyName} is ready. Set a password to sign in.`}
        />
        <SetPasswordForm token={state.token} email={state.email} />
      </PublicPage>
    );
  }

  if (state.status === 'ready') {
    return (
      <PublicPage>
        <Message
          title="Your workspace is ready"
          body={`Sign in to ${state.companyName} with ${state.email}. If you have not set a password yet, use the link in your welcome email.`}
        />
        <Actions>
          <Button variant="contained" component={RouterLink} to="/login">
            Sign in
          </Button>
        </Actions>
      </PublicPage>
    );
  }

  if (state.status === 'slow') {
    return (
      <PublicPage>
        <Box role="alert">
          <Message
            title="Your payment went through"
            body="We are still setting up your workspace and will email you as soon as it is ready."
          />
          <Actions>
            <Button
              variant="contained"
              onClick={() => {
                setPolled({ status: 'pending' });
                setAttempt((n) => n + 1);
              }}
            >
              Check again
            </Button>
            <Button variant="outlined" href={PHONE_HREF}>
              {PHONE_LABEL}
            </Button>
          </Actions>
        </Box>
      </PublicPage>
    );
  }

  if (state.status === 'unknown') {
    return (
      <PublicPage>
        <Box role="alert">
          <Message
            title="We could not find that checkout"
            body={`If you just paid, check your email for a link to set your password, or call Nick at ${PHONE_LABEL}.`}
          />
          <Actions>
            <Button variant="contained" component={RouterLink} to="/login">
              Sign in
            </Button>
            <Button variant="outlined" href={PHONE_HREF}>
              {PHONE_LABEL}
            </Button>
          </Actions>
        </Box>
      </PublicPage>
    );
  }

  return (
    <PublicPage>
      <Box role="status" sx={{ mt: 3 }}>
        <CircularProgress size={28} />
        <Typography sx={{ mt: 1.5, fontWeight: 600 }}>Setting up your workspace</Typography>
        <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
          Your payment went through. This takes a few seconds.
        </Typography>
      </Box>
    </PublicPage>
  );
};

export default WelcomePage;
